<template>
  <AdminLayout>
    <div class="p-6">
      <!-- 헤더 -->
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-2xl font-bold text-slate-800">파트너 건의/신고 관리</h1>
          <p class="text-sm text-slate-500 mt-1">파트너의 건의사항과 신고를 확인하고 답변합니다</p>
        </div>
      </div>

      <!-- 통계 카드 -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div class="bg-white rounded-2xl p-4 border border-slate-100">
          <p class="text-xs text-slate-500 mb-1">전체</p>
          <p class="text-2xl font-bold text-slate-800">{{ totalCount }}</p>
        </div>
        <div class="bg-white rounded-2xl p-4 border border-slate-100">
          <p class="text-xs text-slate-500 mb-1">접수됨</p>
          <p class="text-2xl font-bold text-amber-600">{{ pendingCount }}</p>
        </div>
        <div class="bg-white rounded-2xl p-4 border border-slate-100">
          <p class="text-xs text-slate-500 mb-1">처리완료</p>
          <p class="text-2xl font-bold text-green-600">{{ resolvedCount }}</p>
        </div>
        <div class="bg-white rounded-2xl p-4 border border-slate-100">
          <p class="text-xs text-slate-500 mb-1">처리율</p>
          <p class="text-2xl font-bold text-blue-600">{{ resolveRate }}%</p>
        </div>
      </div>

      <!-- 필터 탭 (상태) -->
      <div class="flex flex-wrap gap-2 mb-4">
        <button
          :class="[
            'px-4 py-2 rounded-full text-sm font-medium transition',
            filterStatus === null
              ? 'bg-blue-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          ]"
          @click="changeFilter(null)"
        >
          전체
        </button>
        <button
          :class="[
            'px-4 py-2 rounded-full text-sm font-medium transition',
            filterStatus === 'pending'
              ? 'bg-amber-500 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          ]"
          @click="changeFilter('pending')"
        >
          접수됨
        </button>
        <button
          :class="[
            'px-4 py-2 rounded-full text-sm font-medium transition',
            filterStatus === 'in_progress'
              ? 'bg-blue-500 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          ]"
          @click="changeFilter('in_progress')"
        >
          처리중
        </button>
        <button
          :class="[
            'px-4 py-2 rounded-full text-sm font-medium transition',
            filterStatus === 'resolved'
              ? 'bg-green-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          ]"
          @click="changeFilter('resolved')"
        >
          처리완료
        </button>
        <button
          :class="[
            'px-4 py-2 rounded-full text-sm font-medium transition',
            filterStatus === 'rejected'
              ? 'bg-red-500 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          ]"
          @click="changeFilter('rejected')"
        >
          반려
        </button>

        <!-- 카테고리 필터 구분 -->
        <span class="w-px h-8 bg-slate-200 mx-1 self-center"></span>

        <button
          :class="[
            'px-4 py-2 rounded-full text-sm font-medium transition',
            filterCategory === null
              ? 'bg-blue-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          ]"
          @click="changeCategory(null)"
        >
          전체 카테고리
        </button>
        <button
          v-for="cat in FEEDBACK_CATEGORIES"
          :key="cat.id"
          :class="[
            'px-4 py-2 rounded-full text-sm font-medium transition',
            filterCategory === cat.id
              ? 'bg-blue-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          ]"
          @click="changeCategory(cat.id)"
        >
          {{ cat.label }}
        </button>
      </div>

      <!-- 로딩 -->
      <div v-if="loading && feedbacks.length === 0" class="flex justify-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
      </div>

      <!-- 목록 -->
      <div v-else-if="filteredFeedbacks.length > 0" class="space-y-3">
        <div
          v-for="feedback in filteredFeedbacks"
          :key="feedback.id"
          class="bg-white rounded-2xl border border-slate-100 overflow-hidden cursor-pointer hover:border-blue-300 transition"
          @click="openDetailModal(feedback)"
        >
          <div class="p-4">
            <div class="flex items-start gap-3">
              <!-- 상태 아이콘 -->
              <div
                :class="[
                  'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
                  getStatusIconBg(feedback.status)
                ]"
              >
                <!-- 접수됨 -->
                <svg
                  v-if="feedback.status === 'pending'"
                  class="w-5 h-5 text-amber-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <!-- 처리중 -->
                <svg
                  v-else-if="feedback.status === 'in_progress'"
                  class="w-5 h-5 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
                <!-- 처리완료 -->
                <svg
                  v-else-if="feedback.status === 'resolved'"
                  class="w-5 h-5 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
                <!-- 반려 -->
                <svg
                  v-else
                  class="w-5 h-5 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </div>

              <!-- 내용 -->
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1">
                  <span class="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                    {{ getCategoryLabel(feedback.category) }}
                  </span>
                  <span
                    :class="[
                      'px-2 py-0.5 text-xs font-medium rounded-full',
                      getStatusBadgeClass(feedback.status)
                    ]"
                  >
                    {{ getStatusLabel(feedback.status) }}
                  </span>
                </div>
                <h3 class="font-bold text-slate-800 mb-1 truncate">{{ feedback.title }}</h3>
                <p class="text-sm text-slate-500 line-clamp-1">{{ feedback.content }}</p>
                <div class="flex items-center gap-3 mt-2 text-xs text-slate-400">
                  <span>{{ feedback.partnerEmail }}</span>
                  <span>{{ formatDate(feedback.createdAt) }}</span>
                </div>
              </div>

              <!-- 화살표 -->
              <svg class="w-5 h-5 text-slate-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- 빈 상태 -->
      <div v-else class="text-center py-12 bg-white rounded-2xl border border-slate-100">
        <div class="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"/>
          </svg>
        </div>
        <h3 class="text-lg font-bold text-slate-800 mb-2">건의/신고가 없습니다</h3>
        <p class="text-sm text-slate-500">접수된 건의/신고가 없습니다</p>
      </div>

      <!-- 상세/답변 모달 -->
      <div
        v-if="showDetailModal"
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
        @click.self="closeDetailModal"
      >
        <div class="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div class="p-6 border-b border-slate-100">
            <div class="flex items-center justify-between">
              <h2 class="text-xl font-bold text-slate-800">건의/신고 상세</h2>
              <button
                class="p-2 text-slate-400 hover:text-slate-600 rounded-lg"
                @click="closeDetailModal"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>

          <div class="p-6" v-if="selectedFeedback">
            <!-- 건의/신고 정보 -->
            <div class="mb-6">
              <div class="flex items-center gap-2 mb-3">
                <span class="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                  {{ getCategoryLabel(selectedFeedback.category) }}
                </span>
                <span
                  :class="[
                    'px-2 py-0.5 text-xs font-medium rounded-full',
                    getStatusBadgeClass(selectedFeedback.status)
                  ]"
                >
                  {{ getStatusLabel(selectedFeedback.status) }}
                </span>
              </div>
              <h3 class="text-lg font-bold text-slate-800 mb-2">{{ selectedFeedback.title }}</h3>
              <div class="flex items-center gap-3 text-sm text-slate-500 mb-4">
                <span>{{ selectedFeedback.partnerEmail }}</span>
                <span>{{ formatDate(selectedFeedback.createdAt) }}</span>
              </div>
              <div class="p-4 bg-slate-50 rounded-xl text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{{ selectedFeedback.content }}</div>
            </div>

            <!-- 기존 답변 -->
            <div v-if="selectedFeedback.adminReply" class="mb-6">
              <h4 class="text-sm font-semibold text-slate-700 mb-2">관리자 답변</h4>
              <div class="p-4 bg-blue-50 rounded-xl text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{{ selectedFeedback.adminReply }}</div>
            </div>

            <!-- 답변 작성/수정 -->
            <div class="space-y-4">
              <h4 class="text-sm font-semibold text-slate-700">
                {{ selectedFeedback.adminReply ? '답변 수정' : '답변 작성' }}
              </h4>
              <textarea
                v-model="replyContent"
                rows="5"
                placeholder="답변 내용을 입력하세요..."
                class="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition resize-none text-sm"
              ></textarea>

              <!-- 상태 선택 -->
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">처리 상태</label>
                <div class="flex gap-2">
                  <button
                    @click="replyStatus = 'in_progress'"
                    class="px-4 py-2 rounded-full text-sm font-medium transition border"
                    :class="replyStatus === 'in_progress' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'"
                  >
                    처리중
                  </button>
                  <button
                    @click="replyStatus = 'resolved'"
                    class="px-4 py-2 rounded-full text-sm font-medium transition border"
                    :class="replyStatus === 'resolved' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-slate-600 border-slate-200 hover:border-green-300'"
                  >
                    처리완료
                  </button>
                  <button
                    @click="replyStatus = 'rejected'"
                    class="px-4 py-2 rounded-full text-sm font-medium transition border"
                    :class="replyStatus === 'rejected' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-slate-600 border-slate-200 hover:border-red-300'"
                  >
                    반려
                  </button>
                </div>
              </div>

              <button
                class="w-full py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition disabled:opacity-50"
                :disabled="!replyContent.trim() || submitting"
                @click="submitReply"
              >
                {{ submitting ? '저장 중...' : '답변 저장' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 토스트 -->
      <transition name="toast">
        <div
          v-if="showToast"
          :class="[
            'fixed bottom-6 left-1/2 -translate-x-1/2 px-6 py-3 rounded-full shadow-lg z-50',
            toastType === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          ]"
        >
          {{ toastMessage }}
        </div>
      </transition>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useFeedback, FEEDBACK_CATEGORIES, FEEDBACK_STATUS } from '@/composables/partners/useFeedback'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'

export default {
  name: 'PartnerFeedbackManageView',
  components: {
    AdminLayout
  },
  setup() {
    const { loading, getAllFeedbacks, updateFeedbackStatus } = useFeedback()

    const feedbacks = ref([])
    const filterStatus = ref(null)
    const filterCategory = ref(null)
    const submitting = ref(false)

    // 통계
    const totalCount = ref(0)
    const pendingCount = ref(0)
    const resolvedCount = ref(0)

    // 상세 모달
    const showDetailModal = ref(false)
    const selectedFeedback = ref(null)
    const replyContent = ref('')
    const replyStatus = ref('resolved')

    // 토스트
    const showToast = ref(false)
    const toastMessage = ref('')
    const toastType = ref('success')

    // 처리율
    const resolveRate = computed(() => {
      if (totalCount.value === 0) return 0
      return Math.round((resolvedCount.value / totalCount.value) * 100)
    })

    const filteredFeedbacks = computed(() => {
      let result = feedbacks.value

      if (filterStatus.value) {
        result = result.filter(f => f.status === filterStatus.value)
      }

      if (filterCategory.value) {
        result = result.filter(f => f.category === filterCategory.value)
      }

      return result
    })

    // 날짜 포맷
    const formatDate = (dateStr) => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    // 토스트 표시
    const showToastMessage = (message, type = 'success') => {
      toastMessage.value = message
      toastType.value = type
      showToast.value = true
      setTimeout(() => {
        showToast.value = false
      }, 3000)
    }

    // 목록 로드
    const loadFeedbacks = async () => {
      try {
        const result = await getAllFeedbacks()
        feedbacks.value = result.feedbacks
        loadStats()
      } catch (err) {
        console.error('Failed to load feedbacks:', err)
      }
    }

    // 통계 계산
    const loadStats = () => {
      totalCount.value = feedbacks.value.length
      pendingCount.value = feedbacks.value.filter(f => f.status === 'pending').length
      resolvedCount.value = feedbacks.value.filter(f => f.status === 'resolved').length
    }

    // 필터 변경
    const changeFilter = (status) => {
      filterStatus.value = status
    }

    const changeCategory = (category) => {
      filterCategory.value = category
    }

    // 카테고리 라벨
    const getCategoryLabel = (categoryId) => {
      const cat = FEEDBACK_CATEGORIES.find(c => c.id === categoryId)
      return cat ? cat.label : categoryId
    }

    // 상태 라벨
    const getStatusLabel = (status) => {
      return FEEDBACK_STATUS[status]?.label || status
    }

    // 상태 배지 클래스
    const getStatusBadgeClass = (status) => {
      const map = {
        pending: 'bg-amber-100 text-amber-600',
        in_progress: 'bg-blue-100 text-blue-600',
        resolved: 'bg-green-100 text-green-600',
        rejected: 'bg-red-100 text-red-600'
      }
      return map[status] || 'bg-slate-100 text-slate-600'
    }

    // 상태 아이콘 배경
    const getStatusIconBg = (status) => {
      const map = {
        pending: 'bg-amber-100',
        in_progress: 'bg-blue-100',
        resolved: 'bg-green-100',
        rejected: 'bg-red-100'
      }
      return map[status] || 'bg-slate-100'
    }

    // 상세 모달 열기
    const openDetailModal = (feedback) => {
      selectedFeedback.value = feedback
      replyContent.value = feedback.adminReply || ''
      replyStatus.value = feedback.status === 'pending' ? 'resolved' : feedback.status
      showDetailModal.value = true
    }

    // 상세 모달 닫기
    const closeDetailModal = () => {
      showDetailModal.value = false
      selectedFeedback.value = null
      replyContent.value = ''
    }

    // 답변 저장
    const submitReply = async () => {
      if (!replyContent.value.trim() || !selectedFeedback.value) return

      submitting.value = true
      try {
        await updateFeedbackStatus(
          selectedFeedback.value.id,
          replyStatus.value,
          replyContent.value
        )
        showToastMessage('답변이 저장되었습니다')
        closeDetailModal()
        await loadFeedbacks()
      } catch (err) {
        showToastMessage('저장에 실패했습니다: ' + err.message, 'error')
      } finally {
        submitting.value = false
      }
    }

    onMounted(() => {
      loadFeedbacks()
    })

    return {
      loading,
      feedbacks,
      filteredFeedbacks,
      filterStatus,
      filterCategory,
      submitting,
      totalCount,
      pendingCount,
      resolvedCount,
      resolveRate,
      showDetailModal,
      selectedFeedback,
      replyContent,
      replyStatus,
      showToast,
      toastMessage,
      toastType,
      FEEDBACK_CATEGORIES,
      FEEDBACK_STATUS,
      getCategoryLabel,
      getStatusLabel,
      getStatusBadgeClass,
      getStatusIconBg,
      formatDate,
      changeFilter,
      changeCategory,
      openDetailModal,
      closeDetailModal,
      submitReply
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* 토스트 애니메이션 */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}
</style>
